import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  MaterialForm,
  MaterialFormSchema,
  MaterialFormValues,
} from 'forms/MaterialForm'
import {
  Material,
  MaterialInsertType,
  MaterialSetType,
  UserRole,
} from 'gql.generated'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  ReadMaterialsQueryVariables,
  useCreateMaterialMutation,
  useDeleteMaterialMutation,
  useReadMaterialsQuery,
  useUpdateMaterialMutation,
} from './AdminMaterialsView.generated'
import { MaterialList } from './MaterialList'

const AdminMaterialsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createMaterial] = useCreateMaterialMutation()
  const [updateMaterial] = useUpdateMaterialMutation()
  const [deleteMaterial] = useDeleteMaterialMutation()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [routeParams, updateRouteParams] = useRouteParams(
    'admin.data.materials',
    {
      search: undefined,
    }
  )

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.material.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  const { loading, error, refetch, data } = useReadMaterialsQuery({
    variables: { search: routeParams.search },
  })

  const items =
    data && data.materials ? data.materials.filter((x) => !x._deleted) : []

  const [selection, selectedItems] = useSelection<Material>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const handleSubmit = async (
    values: MaterialFormValues,
    formikActions: FormikActions<MaterialFormValues>
  ) => {
    formikActions.setSubmitting(true)

    if (formType === FormType.insert) {
      await createMaterial({
        variables: { input: values as MaterialInsertType },
      })
    } else if (formType === FormType.update) {
      await updateMaterial({
        variables: { id: selectedItemId, update: values as MaterialSetType },
      })
    } else {
      throw new Error(
        ' Custom: Error while OderId creat or update. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch({
      variables: { search: routeParams.search },
    } as ReadMaterialsQueryVariables)
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteMaterial({
        variables: { ids },
      })
    }
    refetch({
      variables: { search: routeParams.search },
    } as ReadMaterialsQueryVariables)
    hideDialog()
  }

  /** commands */
  useCreateCommand(
    'material',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({} as MaterialInsertType)
      },
    },
    [setFormType]
  )
  useEditCommand(
    { name: 'material', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], ['id', '__typename']) as MaterialSetType
        )
      },
    },
    [selectedItems, setFormType]
  )
  // useDeleteCommand(
  //   { name: 'material', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <MaterialList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                setSelectedItemId(selectedItems[0]._id)
                showPanel(
                  omitDeep(selectedItems[0], [
                    'id',
                    '__typename',
                  ]) as MaterialSetType
                )
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new MaterialFormSchema()}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.material.edit')
            : t('model.material.create')
        }
      >
        <MaterialForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminMaterialsView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminMaterialsListView />
    </StickyPane>
  </ViewAuthorizer>
)
